/**
 * Padel USA — Blog predictive search.
 * Dossier: custom-code-padelusa/features/blog-index-design.md
 *
 * Scopes Shopify predictive search to ARTICLES (the theme's /search results page is
 * product-only) and renders a clean dropdown of matching blog posts under the blog
 * search input. Self-contained: no impact on the product search page.
 * Results are built with safe DOM methods (textContent), never innerHTML.
 */
const DEBOUNCE_MS = 200;
const MIN_CHARS = 2;
const LIMIT = 8;

class BlogSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.blog-search__input');
    this.results = this.querySelector('.blog-search__results');
    this.form = this.querySelector('.blog-search__form');
    if (!this.input || !this.results || !this.form) return;

    this.suggestUrl = (this.dataset.suggestUrl || '/search/suggest') + '.json';
    this.items = [];
    this.active = -1;
    this.controller = null;

    const debounced = this.debounce(() => this.search(), DEBOUNCE_MS);
    this.input.addEventListener('input', debounced);
    this.input.addEventListener('keydown', (e) => this.onKeydown(e));
    this.input.addEventListener('focus', () => { if (this.items.length) this.open(); });
    // Plain Enter submits the form to /search?type=article (full results page). Enter on a
    // highlighted suggestion opens that article instead (handled in onKeydown).
    document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); });
  }

  debounce(fn, ms) {
    let t;
    return () => { clearTimeout(t); t = setTimeout(fn, ms); };
  }

  async search() {
    const q = this.input.value.trim();
    if (q.length < MIN_CHARS) { this.items = []; this.close(); return; }
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    const url = `${this.suggestUrl}?q=${encodeURIComponent(q)}` +
      `&resources[type]=article&resources[limit]=${LIMIT}`;
    try {
      const res = await fetch(url, { signal: this.controller.signal, headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      this.items = (data.resources && data.resources.results && data.resources.results.articles) || [];
      this.active = -1;
      this.render(q);
    } catch (e) {
      if (e.name !== 'AbortError') this.close();
    }
  }

  render(q) {
    this.results.replaceChildren();
    if (!this.items.length) {
      const empty = document.createElement('p');
      empty.className = 'blog-search__empty';
      empty.textContent = `No articles match “${q}”.`;
      this.results.appendChild(empty);
      this.open();
      return;
    }
    const frag = document.createDocumentFragment();
    this.items.forEach((article, i) => {
      const link = document.createElement('a');
      link.className = 'blog-search__result';
      link.setAttribute('role', 'option');
      link.id = `blog-search-opt-${i}`;
      // Shopify suggest returns same-origin relative paths; guard against anything else.
      link.href = (typeof article.url === 'string' && article.url.charAt(0) === '/') ? article.url : '#';
      link.textContent = article.title || '';
      frag.appendChild(link);
    });
    this.results.appendChild(frag);
    this.open();
  }

  open() {
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.active = -1;
    this.input.removeAttribute('aria-activedescendant');
  }

  highlight() {
    const opts = this.results.querySelectorAll('.blog-search__result');
    opts.forEach((o, i) => o.classList.toggle('is-active', i === this.active));
    if (this.active >= 0 && opts[this.active]) {
      this.input.setAttribute('aria-activedescendant', opts[this.active].id);
      opts[this.active].scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  onKeydown(e) {
    if (e.key === 'Escape') { this.close(); return; }
    if (this.results.hidden || !this.items.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.active = Math.min(this.active + 1, this.items.length - 1);
      this.highlight();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.active = Math.max(this.active - 1, -1);
      this.highlight();
    } else if (e.key === 'Enter' && this.active >= 0) {
      e.preventDefault();
      window.location.href = this.items[this.active].url;
    }
  }
}

if (!customElements.get('blog-search')) {
  customElements.define('blog-search', BlogSearch);
}
